/**
 * Asset depreciation (AST-20) — a schedule per asset (from the category default, overridable per
 * asset), the current book value, and a report per period and currency. All money is a Decimal
 * string with its currency; nothing here is a JS number.
 *
 * Depreciable amount = purchase value − salvage value, spread over `periodYears` from the start
 * date (the asset's `depreciationStartDate`, else its purchase date). Methods (see
 * `ASSET_DEPRECIATION_METHODS`):
 *   - `none`                 — no depreciation; book value stays at the purchase value.
 *   - `straight_line`        — the depreciable amount accrues evenly per COMPLETED month.
 *   - `annual_amortization`  — the same total, booked in whole yearly instalments on each
 *                              anniversary of the start date.
 * A month (or year) completes on the same day-of-month as the start date, clamped to the last day
 * of shorter months. Fiscal-year periods follow the legal entity's `fiscal_year_start_month`
 * (CLAUDE.md invariant 4).
 *
 * Reading a schedule or a report needs `asset.financial:read` AND row-level `asset:read`;
 * changing an asset's depreciation override needs `asset:financial:manage`. The CSV export adds
 * `asset:export`. Its own router so the `/asset-management/depreciation` prefix never overlaps the
 * other Administration tabs.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  assetDepreciationMethodSchema,
  currencySchema,
  dateOnlySchema,
  decimalStringSchema,
  errorBodySchema,
  uuidSchema,
} from '../schemas/common.js';
import { routeMeta } from './meta.js';
import { assetCsvExportSchema } from './vendor.contract.js';

const c = initContract();

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------

export const depreciationProfileSchema = z.object({
  method: assetDepreciationMethodSchema,
  periodYears: z.number().int().nullable(),
  /** In the asset's purchase currency. "0.0000" when none is set. */
  salvageValue: decimalStringSchema,
  startDate: dateOnlySchema.nullable(),
  /** `asset_override` when any of the asset's own depreciation fields is set. */
  source: z.enum(['category_default', 'asset_override']),
});
export type DepreciationProfile = z.infer<typeof depreciationProfileSchema>;

export const depreciationSchedulePeriodSchema = z.object({
  /** Fiscal-year label, e.g. "FY2026" (starts in the legal entity's fiscal-year-start month). */
  label: z.string(),
  periodStart: dateOnlySchema,
  periodEnd: dateOnlySchema,
  openingValue: decimalStringSchema,
  charge: decimalStringSchema,
  closingValue: decimalStringSchema,
});
export type DepreciationSchedulePeriod = z.infer<typeof depreciationSchedulePeriodSchema>;

export const assetDepreciationSchema = z.object({
  assetId: uuidSchema,
  assetCode: z.string(),
  assetName: z.string(),
  /** The purchase currency; null when the asset has no purchase value recorded. */
  currency: currencySchema.nullable(),
  purchaseValue: decimalStringSchema.nullable(),
  profile: depreciationProfileSchema,
  /** False when no schedule can be produced; `reason` says why. */
  scheduled: z.boolean(),
  reason: z
    .enum(['no_purchase_value', 'no_start_date', 'method_none', 'no_period'])
    .nullable(),
  asOf: dateOnlySchema,
  /** Null only when there is no purchase value to depreciate. */
  currentBookValue: decimalStringSchema.nullable(),
  accumulatedDepreciation: decimalStringSchema.nullable(),
  /** Fiscal years from the start date to the end of the depreciation period; empty when not scheduled. */
  schedule: z.array(depreciationSchedulePeriodSchema),
});
export type AssetDepreciation = z.infer<typeof assetDepreciationSchema>;

/**
 * Every field optional; `null` clears the asset's own value (back to the category default / none).
 * `method: null` together with the other three cleared removes the override entirely.
 */
export const setAssetDepreciationProfileBodySchema = z
  .object({
    method: assetDepreciationMethodSchema.nullable().optional(),
    periodYears: z.number().int().min(1).max(100).nullable().optional(),
    salvageValue: decimalStringSchema
      .refine((v) => Number.parseFloat(v) >= 0, 'Must not be negative')
      .nullable()
      .optional(),
    startDate: dateOnlySchema.nullable().optional(),
  })
  .strict();
export type SetAssetDepreciationProfileBody = z.infer<typeof setAssetDepreciationProfileBodySchema>;

export const depreciationGranularitySchema = z.enum(['month', 'quarter', 'fiscal_year']);
export type DepreciationGranularity = z.infer<typeof depreciationGranularitySchema>;

export const depreciationReportQuerySchema = z.object({
  from: dateOnlySchema,
  to: dateOnlySchema,
  granularity: depreciationGranularitySchema.default('month'),
  /** Restrict to one currency (an asset's purchase currency). */
  currency: currencySchema.optional(),
  categoryId: uuidSchema.optional(),
  locationId: uuidSchema.optional(),
  /** The asset's effective legal entity (set on the asset, else its location's). Required for `fiscal_year` when entities differ in fiscal calendar. */
  legalEntityId: uuidSchema.optional(),
});
export type DepreciationReportQuery = z.infer<typeof depreciationReportQuerySchema>;

export const depreciationReportPeriodSchema = z.object({
  label: z.string(),
  periodStart: dateOnlySchema,
  periodEnd: dateOnlySchema,
  /** Book value at the start of the period (assets not yet on the books count 0). */
  openingValue: decimalStringSchema,
  /** Cost of assets whose depreciation started in this period. */
  additions: decimalStringSchema,
  charge: decimalStringSchema,
  /** opening + additions − charge. */
  closingValue: decimalStringSchema,
  /** Assets with a non-zero charge in this period. */
  assetCount: z.number().int().nonnegative(),
});

export const depreciationReportCurrencySchema = z.object({
  currency: currencySchema,
  periods: z.array(depreciationReportPeriodSchema),
  totalCharge: decimalStringSchema,
  openingValue: decimalStringSchema,
  closingValue: decimalStringSchema,
  assetCount: z.number().int().nonnegative(),
});

export const depreciationReportSchema = z.object({
  from: dateOnlySchema,
  to: dateOnlySchema,
  granularity: depreciationGranularitySchema,
  currencies: z.array(depreciationReportCurrencySchema),
  /** In-scope assets left out because they have no purchase value / currency / start date. */
  excludedAssetCount: z.number().int().nonnegative(),
});
export type DepreciationReport = z.infer<typeof depreciationReportSchema>;

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const assetDepreciationContract = c.router(
  {
    // Literal paths first; `/assets/:assetId` routes are a different shape from `/report*`.
    getDepreciationReport: {
      method: 'GET',
      path: '/report',
      query: depreciationReportQuerySchema,
      summary: 'Depreciation per period and currency over the assets the caller can see (asset.financial:read)',
      metadata: routeMeta(['asset.financial:read', 'asset:read']),
      responses: {
        200: depreciationReportSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
    },
    exportDepreciationReport: {
      method: 'GET',
      path: '/report/export',
      query: depreciationReportQuerySchema,
      summary: 'Export the depreciation report as CSV (asset:export + asset.financial:read)',
      metadata: routeMeta(['asset:export', 'asset.financial:read', 'asset:read']),
      responses: {
        200: assetCsvExportSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
    },
    getAssetDepreciation: {
      method: 'GET',
      path: '/assets/:assetId',
      pathParams: z.object({ assetId: uuidSchema }),
      summary: "One asset's depreciation schedule and current book value (asset.financial:read)",
      metadata: routeMeta(['asset.financial:read', 'asset:read']),
      responses: {
        200: assetDepreciationSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
    },
    setAssetDepreciationProfile: {
      method: 'PUT',
      path: '/assets/:assetId/profile',
      pathParams: z.object({ assetId: uuidSchema }),
      body: setAssetDepreciationProfileBodySchema,
      summary: "Override an asset's depreciation method, period, salvage value or start date (asset:financial:manage)",
      metadata: routeMeta('asset:financial:manage'),
      responses: {
        200: assetDepreciationSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
    },
  },
  { pathPrefix: '/asset-management/depreciation' },
);
